import React, { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft, Check, Loader2, AlertCircle,
  TrendingUp, Sparkles, Wrench, Target,
  Award, Rocket, HandCoins, Gauge
} from 'lucide-react';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { useToast } from '../hooks/use-toast';
import useRevealObserver from '../hooks/useRevealObserver';
import AITestimonyBlock from '../components/AITestimonyBlock';
import { submitLead } from '../lib/api';

/**
 * /experts — Landing page for AVATAR A: "The Expert with Vision".
 * Persona: methodology owners, top-tier professionals, expert coaches / consultants
 * who have real IP and results but lack business infrastructure.
 * Design brief: Premium business lounge — deep navy, cream, gold/bronze accents,
 * clean airy typography, vision + partnership.
 * Lead source: 'expert-landing' (NEW).
 */
const Experts = () => {
  useRevealObserver();
  const { toast } = useToast();

  const [form, setForm] = useState({
    fullName: '',
    expertise: '',
    assetUrl: '',
    budgetFit: '', // 'yes' | 'not-yet'
    phone: '',
    email: '',
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const formRef = useRef(null);
  const errorRef = useRef(null);

  const handle = (key) => (e) => {
    setForm({ ...form, [key]: e.target ? e.target.value : e });
    if (errors[key]) setErrors({ ...errors, [key]: undefined });
  };

  const validate = () => {
    const errs = {};
    if (!form.fullName.trim()) errs.fullName = 'יש להזין שם מלא';
    if (!form.expertise.trim()) errs.expertise = 'נודה לתשובה קצרה על תחום המומחיות';
    if (!form.email.trim()) errs.email = 'יש להזין כתובת דוא״ל';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) errs.email = 'כתובת דוא״ל לא תקינה';
    if (!form.phone.trim()) errs.phone = 'יש להזין מספר טלפון';
    if (!form.budgetFit) errs.budgetFit = 'נודה לתשובה על שאלת ההתאמה';
    return errs;
  };

  const submit = async (e) => {
    e.preventDefault();
    const errs = validate();
    setErrors(errs);
    if (Object.keys(errs).length > 0) {
      toast({ title: 'יש להשלים את השדות המסומנים' });
      setTimeout(() => errorRef.current?.focus(), 50);
      return;
    }
    setSubmitting(true);
    try {
      const budgetLabel =
        form.budgetFit === 'yes'
          ? 'כן — התקציב תואם'
          : 'עדיין לא — אשמח למידע נוסף';
      await submitLead({
        fullName: form.fullName,
        email: form.email,
        phone: form.phone,
        role: 'מומחה בעל שיטה',
        company: form.assetUrl || undefined,
        challenge: `תחום מומחיות: ${form.expertise}`,
        improve: budgetLabel,
        source: 'expert-landing',
      });
      setSubmitted(true);
    } catch (err) {
      toast({
        title: 'שגיאה בשליחת הטופס',
        description: err?.response?.data?.detail || 'נסו שוב בעוד רגע',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const scrollToForm = () => formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });

  return (
    <div className="pt-20">
      {/* ============ SECTION 1 · HERO ============ */}
      <section
        className="relative overflow-hidden bg-cream"
        aria-labelledby="experts-hero-heading"
        data-testid="experts-hero"
      >
        {/* Abstract growth curve — subtle background */}
        <div
          aria-hidden="true"
          className="absolute inset-0 opacity-[0.06] pointer-events-none"
          style={{
            backgroundImage:
              "radial-gradient(circle at 85% 20%, rgba(201,169,97,0.55) 0%, transparent 40%), radial-gradient(circle at 10% 90%, rgba(15,37,64,0.35) 0%, transparent 45%)",
          }}
        />
        <div className="relative max-w-6xl mx-auto px-6 lg:px-12 py-24 lg:py-32">
          <div className="reveal max-w-4xl text-center mx-auto">
            <div
              className="inline-flex items-center gap-2 text-xs tracking-[0.22em] font-semibold text-bronze mb-6 uppercase"
              data-testid="experts-eyebrow"
            >
              <Award className="w-3.5 h-3.5" aria-hidden="true" />
              <span>Experts · Vision · Business Architecture</span>
            </div>
            <h1
              id="experts-hero-heading"
              className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.15] text-navy mb-8"
            >
              השיטה שלכם כבר משנה חיים.<br className="hidden md:block" />
              עכשיו הגיע הזמן לבנות לה{' '}
              <span className="text-bronze">אדריכלות עסקית</span>.
            </h1>
            <p className="text-lg lg:text-xl leading-relaxed text-navy/80 max-w-3xl mx-auto mb-10">
              יש לכם ידע ששווה זהב ומומחיות נדירה. אנחנו כאן כדי לקחת את החזון
              הזה, ולארוז אותו לתוך תשתית שיווקית (GTM) מהונדסת ומדויקת שתהפוך
              אותו לאופרציה רווחית, יציבה וצומחת.
            </p>
            <button
              type="button"
              onClick={scrollToForm}
              className="btn-cta inline-flex items-center gap-3 bg-navy text-cream px-8 py-4 text-base font-medium hover:bg-navy-700 transition-colors"
              data-testid="experts-hero-cta"
            >
              <span>לתיאום פגישת אפיון ובניית אדריכלות עסקית</span>
              <ArrowLeft className="w-5 h-5" aria-hidden="true" />
            </button>
          </div>
        </div>
      </section>

      {/* AI TESTIMONY (shared GEO block) */}
      <AITestimonyBlock />

      {/* ============ SECTION 2 · THE VISIONARY'S BOTTLENECK ============ */}
      <section className="py-24 lg:py-28 bg-white" aria-labelledby="experts-bottleneck-heading">
        <div className="max-w-6xl mx-auto px-6 lg:px-12">
          <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
            {/* Text — right side in RTL */}
            <div className="lg:col-span-7 reveal">
              <p className="eyebrow text-bronze mb-4">The Visionary&rsquo;s Bottleneck</p>
              <h2
                id="experts-bottleneck-heading"
                className="font-serif text-3xl sm:text-4xl lg:text-5xl text-navy leading-tight mb-6"
              >
                הפער בין האימפקט שאתם מייצרים,<br className="hidden md:block" />
                לשורת ההכנסות שלכם.
              </h2>
              <div className="space-y-5 text-navy/80 text-base lg:text-lg leading-relaxed">
                <p>
                  אתם אנשי מקצוע בכירים. פיתחתם שיטה, צברתם ניסיון ויש לכם קבלות
                  על הצלחות. אתם יודעים בדיוק מה הערך שאתם נותנים לעולם.
                </p>
                <p>
                  אבל כדי להפוך רעיון מצוין או מומחיות אישית לעסק שמייצר הכנסות
                  פרימיום באופן שיטתי, נדרשת תשתית. נדרש אפיון מדויק של קהל היעד,
                  תמחור נכון, מסע לקוח חכם, ומערכת שמזקקת את המסר שלכם ומביאה
                  אליכם את הלקוחות הבשלים ביותר.
                </p>
              </div>
            </div>

            {/* Growth visual */}
            <div className="lg:col-span-5 reveal">
              <div className="relative aspect-square max-w-md mx-auto">
                {/* Growth arrow visual (SVG) */}
                <svg
                  viewBox="0 0 400 400"
                  className="w-full h-full"
                  aria-hidden="true"
                >
                  <defs>
                    <linearGradient id="growth-grad" x1="0%" y1="100%" x2="100%" y2="0%">
                      <stop offset="0%" stopColor="#c9a961" stopOpacity="0.4" />
                      <stop offset="100%" stopColor="#c9a961" stopOpacity="1" />
                    </linearGradient>
                  </defs>
                  {/* Grid */}
                  <g stroke="rgba(15,37,64,0.08)" strokeWidth="1">
                    {[80, 160, 240, 320].map((y) => (
                      <line key={`h-${y}`} x1="40" y1={y} x2="380" y2={y} />
                    ))}
                    {[80, 160, 240, 320].map((x) => (
                      <line key={`v-${x}`} x1={x} y1="40" x2={x} y2="360" />
                    ))}
                  </g>
                  {/* Axes */}
                  <line x1="40" y1="40" x2="40" y2="360" stroke="#0f2540" strokeWidth="2" />
                  <line x1="40" y1="360" x2="380" y2="360" stroke="#0f2540" strokeWidth="2" />
                  {/* Growth curve */}
                  <path
                    d="M 60 340 C 120 335, 180 300, 220 240 S 320 100, 370 60"
                    stroke="url(#growth-grad)"
                    strokeWidth="4"
                    fill="none"
                    strokeLinecap="round"
                  />
                  {/* Endpoint dot */}
                  <circle cx="370" cy="60" r="8" fill="#c9a961" />
                  <circle cx="370" cy="60" r="14" fill="#c9a961" fillOpacity="0.25" />
                </svg>
                {/* Labels */}
                <div className="absolute top-4 left-4 text-right">
                  <p className="font-serif text-3xl text-navy">GTM</p>
                  <p className="text-xs text-navy/60 tracking-wide">Architecture</p>
                </div>
                <div className="absolute bottom-4 right-4">
                  <p className="text-xs text-navy/50">אקספרטיזה</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ SECTION 3 · METHOD (3 blocks) ============ */}
      <section className="py-24 lg:py-28 bg-cream" aria-labelledby="experts-method-heading">
        <div className="max-w-6xl mx-auto px-6 lg:px-12">
          <div className="reveal max-w-3xl mb-16">
            <p className="eyebrow text-bronze mb-4">The Mechanism</p>
            <h2
              id="experts-method-heading"
              className="font-serif text-3xl sm:text-4xl lg:text-5xl text-navy leading-tight"
            >
              איך אנחנו הופכים מומחיות למכונת צמיחה?
            </h2>
          </div>

          <div className="grid lg:grid-cols-3 gap-6 lg:gap-8">
            {[
              {
                num: '01',
                icon: Target,
                title: 'אפיון ותכנון הנדסי (GTM)',
                text: 'אנחנו לא קופצים למודעות. אנחנו מתחילים באסטרטגיה — מנתחים את השוק, ממצבים את המוצר שלכם כפרימיום, ובונים את "הסוס הטרויאני" השיווקי שיפרוץ את הדרך.',
              },
              {
                num: '02',
                icon: Wrench,
                title: 'סוכנות Turn-Key מלאה',
                text: 'אנחנו מורידים מכם את העומס התפעולי. צוות המומחים שלנו — משלב פסיכולוגיית צרכנים חדה עם ניהול פרויקטים בסטנדרט של תשתיות לאומיות — בונה עבורכם את כל המשפך מאפס.',
              },
              {
                num: '03',
                icon: Gauge,
                title: 'שותפות והתייעלות (ROI)',
                text: 'כשהתשתית עומדת, אנחנו מודדים, משפרים ומייעלים כל שלב במערכת, כדי להבטיח ניצול מקסימלי של המשאבים ותשואה ארוכת טווח.',
              },
            ].map((b, i) => {
              const Icon = b.icon;
              return (
                <article
                  key={b.num}
                  className="bg-white border border-navy/10 p-8 lg:p-9 lift-card reveal"
                  style={{ transitionDelay: `${i * 90}ms` }}
                  data-testid={`experts-method-block-${i}`}
                >
                  <div className="flex items-center justify-between mb-5">
                    <span className="font-serif text-3xl text-bronze">{b.num}</span>
                    <Icon className="w-6 h-6 text-navy/60" aria-hidden="true" />
                  </div>
                  <h3 className="font-serif text-2xl text-navy mb-3 leading-snug">{b.title}</h3>
                  <p className="text-navy/70 text-sm lg:text-base leading-relaxed">{b.text}</p>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      {/* ============ SECTION 4 · CASE STUDY ============ */}
      <section className="py-24 lg:py-28 bg-white" aria-labelledby="experts-case-heading">
        <div className="max-w-4xl mx-auto px-6 lg:px-12">
          <div className="reveal border border-navy/10 bg-cream p-10 lg:p-14 relative">
            <div className="absolute -top-4 right-6 bg-bronze text-navy px-4 py-1.5 text-xs tracking-[0.18em] uppercase font-semibold">
              Case Study
            </div>

            <p className="eyebrow text-navy/50 mb-3 flex items-center gap-2">
              <Sparkles className="w-3.5 h-3.5" aria-hidden="true" />
              <span>90-Day Sprint</span>
            </p>
            <h2
              id="experts-case-heading"
              className="font-serif text-3xl sm:text-4xl lg:text-5xl text-navy leading-tight mb-6"
            >
              מ״רעיון מצוין״ לאופרציה שמייצרת<br className="hidden md:block" />
              לקוחות פרימיום ב-90 יום.
            </h2>

            <div className="space-y-5 text-navy/80 text-base lg:text-lg leading-relaxed">
              <p>
                כשהצוות של <strong className="text-navy">״בית הספר לשליטה בחיים״</strong>
                {' '}הגיע אלינו, הייתה להם שיטה משנת-חיים עבור הייטקיסטים, אך ללא
                מותג או תשתית.
              </p>
              <p>
                בנינו עבורם תכנית פעולה ל-90 יום: יצרנו מהלך אורגני לגיוס
                מתאמני פרימיום ראשונים, ובמקביל בנינו מודל הרצאות B2B בחברות
                ענק שייצר קהל שבוי ואיכותי.
              </p>
              <p>
                <strong className="text-navy">התוצאה?</strong> מעבר מסטטוס
                אנונימי למכונה משומנת שמייצרת ודאות עסקית מוחלטת והחזר השקעה
                חזוי.
              </p>
            </div>

            <div className="mt-8 pt-6 border-t border-navy/10 grid grid-cols-3 gap-4 text-center">
              {[
                { k: '90', l: 'ימים לתכנית פעולה' },
                { k: 'B2B', l: 'הרצאות בחברות ענק' },
                { k: 'ROI', l: 'החזר השקעה חזוי' },
              ].map((s) => (
                <div key={s.l}>
                  <p className="font-serif text-3xl lg:text-4xl text-bronze">{s.k}</p>
                  <p className="text-xs text-navy/60 mt-1">{s.l}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ============ SECTION 5 · SKIN IN THE GAME ============ */}
      <section
        className="py-24 lg:py-28 bg-navy text-cream relative overflow-hidden"
        aria-labelledby="experts-skin-heading"
      >
        <div
          aria-hidden="true"
          className="absolute inset-0 opacity-[0.05]"
          style={{
            backgroundImage:
              'radial-gradient(circle at 15% 20%, rgba(201,169,97,0.6) 0%, transparent 30%), radial-gradient(circle at 90% 80%, rgba(201,169,97,0.4) 0%, transparent 35%)',
          }}
        />
        <div className="relative max-w-4xl mx-auto px-6 lg:px-12 text-center reveal">
          <div className="inline-flex items-center gap-2 text-xs tracking-[0.22em] font-semibold text-bronze mb-6 uppercase">
            <HandCoins className="w-3.5 h-3.5" aria-hidden="true" />
            <span>Skin in the Game</span>
          </div>
          <h2
            id="experts-skin-heading"
            className="font-serif text-3xl sm:text-4xl lg:text-5xl leading-tight mb-8"
          >
            כשהשיווק הופך למתמטיקה,<br className="hidden md:block" />
            <span className="text-bronze">אנחנו שמים את הכסף שלנו על השולחן.</span>
          </h2>
          <p className="text-cream/85 text-base lg:text-lg leading-relaxed max-w-3xl mx-auto">
            אנחנו מחפשים שותפים לדרך. מומחים שמוכנים להשקיע בבניית התשתיות
            העסקיות שלהם. עבור השותפים המתאימים שעוברים איתנו את תהליך האפיון
            ובניית האדריכלות העסקית — אנחנו כל כך בטוחים במשפך שהנדסנו, שאנחנו
            אלו שמממנים את קמפיין המדיה.{' '}
            <strong className="text-cream">
              אפס סיכון תזרימי על פרסום בשלב ההשקה.
            </strong>
          </p>

          <div className="mt-10 flex flex-wrap items-center justify-center gap-x-8 gap-y-3 text-sm">
            {[
              { icon: TrendingUp, t: 'תשואה חזויה' },
              { icon: Rocket, t: 'ללא סיכון תזרימי בשלב ההשקה' },
              { icon: Award, t: 'שותפות ארוכת טווח' },
            ].map((it) => {
              const Icon = it.icon;
              return (
                <span key={it.t} className="inline-flex items-center gap-2 text-cream/80">
                  <Icon className="w-4 h-4 text-bronze" aria-hidden="true" />
                  <span>{it.t}</span>
                </span>
              );
            })}
          </div>
        </div>
      </section>

      {/* ============ SECTION 6 · APPLICATION FORM ============ */}
      <section
        ref={formRef}
        className="py-24 lg:py-28 bg-cream border-t border-navy/10"
        aria-labelledby="experts-form-heading"
        data-testid="experts-form-section"
      >
        <div className="max-w-3xl mx-auto px-6 lg:px-12">
          {submitted ? (
            <div className="text-center py-8" data-testid="experts-form-success">
              <div className="w-14 h-14 mx-auto rounded-full bg-bronze/15 text-bronze flex items-center justify-center mb-5">
                <Check className="w-7 h-7" aria-hidden="true" />
              </div>
              <h2 className="font-serif text-3xl lg:text-4xl text-navy mb-3">
                בקשתכם נקלטה
              </h2>
              <p className="text-navy/70 text-base lg:text-lg">
                צוות GAL BLIZOVSKY יחזור אליכם תוך 24 שעות עסקיות לתיאום שיחת האפיון.
              </p>
              <Link
                to="/"
                className="inline-flex items-center gap-2 mt-8 text-sm text-navy hover:text-bronze transition-colors"
              >
                <ArrowLeft className="w-4 h-4" aria-hidden="true" />
                חזרה לעמוד הבית
              </Link>
            </div>
          ) : (
            <form onSubmit={submit} noValidate data-testid="experts-form">
              <div className="text-center mb-10">
                <p className="eyebrow text-bronze mb-4">Business Architecture Application</p>
                <h2 id="experts-form-heading" className="font-serif text-3xl lg:text-4xl text-navy mb-4">
                  מוכנים להקים את התשתית שלכם?
                </h2>
                <p className="text-navy/70 text-base leading-relaxed max-w-2xl mx-auto">
                  בניית אדריכלות עסקית מאפס היא תהליך של הקמת תשתיות שלוקח זמן,
                  משאבים ומחויבות. כדי שנוכל לייצר עבורכם את התוצאות הטובות
                  ביותר, נודה למילוי הפרטים הבאים לקראת שיחת האפיון שלנו.
                </p>
              </div>

              {Object.keys(errors).length > 0 && (
                <div
                  ref={errorRef}
                  tabIndex={-1}
                  role="alert"
                  className="mb-6 p-4 border border-destructive/40 bg-destructive/5 flex items-start gap-3"
                  data-testid="experts-form-errors"
                >
                  <AlertCircle className="w-5 h-5 text-destructive flex-shrink-0 mt-0.5" aria-hidden="true" />
                  <div>
                    <p className="font-medium text-destructive">יש להשלים את השדות הבאים:</p>
                    <ul className="text-sm text-destructive/90 mt-1 list-disc pr-5">
                      {Object.entries(errors).map(([f, m]) => <li key={f}>{m}</li>)}
                    </ul>
                  </div>
                </div>
              )}

              <div className="grid md:grid-cols-2 gap-5">
                <div className="md:col-span-2">
                  <Label htmlFor="exp-name" className="text-navy text-sm mb-2 block">
                    שם מלא <span className="text-bronze">*</span>
                  </Label>
                  <Input
                    id="exp-name"
                    value={form.fullName}
                    onChange={handle('fullName')}
                    autoComplete="name"
                    className="input-clean h-12"
                    aria-invalid={!!errors.fullName}
                    data-testid="experts-input-fullName"
                  />
                </div>
                <div>
                  <Label htmlFor="exp-email" className="text-navy text-sm mb-2 block">
                    דוא״ל <span className="text-bronze">*</span>
                  </Label>
                  <Input
                    id="exp-email"
                    type="email"
                    value={form.email}
                    onChange={handle('email')}
                    autoComplete="email"
                    dir="ltr"
                    className="input-clean h-12"
                    aria-invalid={!!errors.email}
                    data-testid="experts-input-email"
                  />
                </div>
                <div>
                  <Label htmlFor="exp-phone" className="text-navy text-sm mb-2 block">
                    טלפון <span className="text-bronze">*</span>
                  </Label>
                  <Input
                    id="exp-phone"
                    type="tel"
                    value={form.phone}
                    onChange={handle('phone')}
                    autoComplete="tel"
                    dir="ltr"
                    className="input-clean h-12"
                    aria-invalid={!!errors.phone}
                    data-testid="experts-input-phone"
                  />
                </div>
                <div className="md:col-span-2">
                  <Label htmlFor="exp-expertise" className="text-navy text-sm mb-2 block">
                    מה תחום המומחיות / השיטה שלכם? <span className="text-bronze">*</span>
                  </Label>
                  <Textarea
                    id="exp-expertise"
                    value={form.expertise}
                    onChange={handle('expertise')}
                    rows={3}
                    className="input-clean"
                    placeholder="למשל: אימון קוגניטיבי לביצועים גבוהים / מתודולוגיית מכירות B2B / שיטת ליווי מנטלי לספורטאים…"
                    aria-invalid={!!errors.expertise}
                    data-testid="experts-input-expertise"
                  />
                </div>
                <div className="md:col-span-2">
                  <Label htmlFor="exp-asset" className="text-navy text-sm mb-2 block">
                    קישור לנכס דיגיטלי קיים{' '}
                    <span className="text-navy/40 text-xs">(אתר / לינקדאין / פייסבוק — רשות)</span>
                  </Label>
                  <Input
                    id="exp-asset"
                    value={form.assetUrl}
                    onChange={handle('assetUrl')}
                    dir="ltr"
                    className="input-clean h-12"
                    placeholder="https://…"
                    data-testid="experts-input-assetUrl"
                  />
                </div>

                {/* Budget qualification — the polite one */}
                <div className="md:col-span-2">
                  <fieldset>
                    <legend className="text-navy text-sm mb-3 block">
                      תהליך בניית אדריכלות עסקית (GTM) דורש השקעה ראשונית בהקמת
                      התשתיות של <strong>100,000 ₪ ומעלה</strong>. האם זהו סדר
                      הגודל של התקציב שהקציתם לפיתוח העסקי כעת?{' '}
                      <span className="text-bronze">*</span>
                    </legend>
                    <div className="grid sm:grid-cols-2 gap-3">
                      {[
                        { v: 'yes', l: 'כן, זה סדר הגודל שלנו' },
                        { v: 'not-yet', l: 'עדיין לא — אשמח למידע נוסף' },
                      ].map((opt) => {
                        const selected = form.budgetFit === opt.v;
                        return (
                          <label
                            key={opt.v}
                            className={`cursor-pointer border p-4 transition-colors flex items-start gap-3 ${
                              selected
                                ? 'border-bronze bg-bronze/5'
                                : 'border-navy/15 hover:border-navy/40 bg-white'
                            }`}
                          >
                            <input
                              type="radio"
                              name="budgetFit"
                              value={opt.v}
                              checked={selected}
                              onChange={handle('budgetFit')}
                              className="mt-1"
                              style={{ accentColor: '#c9a961' }}
                              data-testid={`experts-input-budget-${opt.v}`}
                            />
                            <span className="text-navy/85 text-sm leading-relaxed">
                              {opt.l}
                            </span>
                          </label>
                        );
                      })}
                    </div>
                  </fieldset>
                </div>
              </div>

              <div className="mt-8 text-center">
                <button
                  type="submit"
                  disabled={submitting}
                  className="btn-cta inline-flex items-center justify-center gap-3 bg-navy text-cream px-10 py-4 text-base font-medium hover:bg-navy-700 transition-colors disabled:opacity-60"
                  data-testid="experts-form-submit"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" aria-hidden="true" />
                      <span>שולח…</span>
                    </>
                  ) : (
                    <>
                      <Award className="w-5 h-5" aria-hidden="true" />
                      <span>תיאום שיחת אפיון עם צוות GAL BLIZOVSKY</span>
                    </>
                  )}
                </button>
                <p className="text-xs text-navy/50 mt-4">
                  הפרטים נשמרים אצלנו בלבד. לא ישותפו עם צד שלישי.
                </p>
              </div>
            </form>
          )}
        </div>
      </section>
    </div>
  );
};

export default Experts;
